import { IconProp } from '@fortawesome/fontawesome-svg-core'
import _ from 'lodash'
import {
  ContentItem,
  ContentItemInput,
  DocumentItem,
  DocumentItemInput,
  ImageItem,
  ItListItem,
  ItListItemType,
  ProductItem,
  ProductItemInput,
  TextItem,
  TextItemInput,
  UpdateItListItemInput,
} from 'types/graphql'

import { PartialWithId } from 'src/types'

import { TypeBuilderProps } from '..'
import { ElementProps } from '../GenericElement/GenericElement'

import * as content from './content'
import * as document from './document'
import * as image from './image'
import * as product from './product'
import * as text from './text'
import * as voiceTip from './voiceTip'

export const itListItemTypes = {
  content,
  product,
  document,
  text,
  voiceTip,
  image,
}

export type ItListItemDetails = {
  product?: PartialWithId<ProductItem>
  content?: PartialWithId<ContentItem>
  document?: PartialWithId<DocumentItem>
  text?: PartialWithId<TextItem>
  voiceTip?: never
  image?: PartialWithId<ImageItem>
}

type ItListItemInputs = {
  product?: ProductItemInput
  content?: ContentItemInput
  document?: DocumentItemInput
  text?: TextItemInput
  voiceTip?: never
  image?: ImageItem
}

export type ItemDescription<ItemType, ItemTypeInput> = {
  type: ItListItemType
  icon: IconProp
  label: string
  thumbnail?: (item: ItListItem) => string | undefined
  Placeholder?: React.FC
  Element?: React.FC<ElementProps>
  Header?: React.FC<ElementProps>
  hasCustomFields: boolean
  generateEmptyItemDetails?: () => Record<string, string | number | boolean>
  convertToGQLInput?: (item: ItemType) => ItemTypeInput
  Builder: React.FC<TypeBuilderProps>
  defaultTitle?: string
}

type ItemDescriptionMap = {
  [K in ItListItemType]: ItemDescription<
    ItListItemDetails[K],
    ItListItemInputs[K]
  >
}

export const itemDescriptionMap: ItemDescriptionMap = Object.entries(
  itListItemTypes
).reduce(
  (acc, itListItemEntry) => ({
    ...acc,
    [itListItemEntry[0]]: itListItemEntry[1].itemDescription,
  }),
  {} as ItemDescriptionMap
)

export const itemDescriptions = Object.values(itListItemTypes).map(
  (itListItem) => itListItem.itemDescription
)

export const convertToGQLInput = (item: ItListItem): UpdateItListItemInput => ({
  ...convertItemTopPropertiesToGQLInput(item),
  ...Object.keys(itListItemTypes).reduce(
    (acc, itemTypeName) =>
      item[itemTypeName]
        ? {
            ...acc,
            [itemTypeName]: itemDescriptionMap[itemTypeName].convertToGQLInput(
              item[itemTypeName]
            ),
          }
        : acc,
    {}
  ),
})

const convertItemTopPropertiesToGQLInput = ({
  __typename,
  type: _type,
  voiceTipUrl: _voiceTipUrl,
  ...item
}: ItListItem): UpdateItListItemInput =>
  _.omit(item, ...Object.keys(itListItemTypes)) as UpdateItListItemInput
